<template lang="pug">
  .b-nodes(
    :style="{'--scale': layoutScale}"
  )
    .b-nodes__container(
      ref="container"
      :style="containerStyle"
    )
      .b-nodes__inner
        .b-nodes__rows
          ConnectorsSVG
          .b-nodes__second-inner
            Node(
              v-if="rootNode"
              :key="rootNode._childrenCount"
              :item="rootNode"
            )
          .b-nodes__trash(
            v-if="getTrash"
          )
            Node(
              v-for="(node, index) in getTrash"
              :key="node.id"
              :item="node"
              isDeleted
            )
            .b-nodes__trash-label Deleted
          ConnectorsSVG(
            :isOverlay="true"
          )
    Controls
    JSON
</template>

<script>
import store from '@/store';
import '@/utils/dragdroptouch';
import ConnectorsSVG from '@/components/connectors/ConnectorsSVG';
import Node from '@/components/node/Node';
import NewNode from '@/components/node/NewNode';
import Controls from '@/components/controls/Controls';
import JSON from '@/components/JSON';
import { mapMutations, mapActions, mapGetters } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import { ELEMENTS_TYPES, ELEMENTS_TYPES_LABELS } from '@/api/elements';
import sleep from '@/utils/sleep';

export default {
  name: 'Nodes',
  components: {
    Node,
    ConnectorsSVG,
    NewNode,
    Controls,
    JSON,
  },
  data: () => ({
    ELEMENTS_TYPES,
    ELEMENTS_TYPES_LABELS,
  }),
  async created() {
    this.resetTempVariables();
    await store.restored;
    await this.checkVersion();
    this.checkRootNode();
  },
  mounted() {
    this.runVisuals();
    this.initEvents();
  },
  computed: {
    ...mapFields('scroll', [
      'scrollPosition',
      'allowDragScroll',
      'isDragScroll',
    ]),
    ...mapFields('nodes', [
      'date',
      'draggingItem',
      'editingNodeText',
      'items',
    ]),
    ...mapGetters('nodes', [
      'rootNode',
      'isDraggingNode',
      'getTrash',
    ]),
    ...mapFields('visualPositions', [
      'positionsUpdatedAt',
      'itemsPositions',
    ]),
    ...mapFields('scale', [
      'layoutScale',
    ]),
    containerStyle() {
      return this.allowDragScroll
        ? this.isDragScroll
          ? {
            cursor: 'grabbing',
            'user-select': 'none',
          }
          : {
            cursor: 'grab',
            'user-select': 'auto',
          }
        : {};
    },
  },
  methods: {
    ...mapMutations('nodes', [
      'resetTempVariables',
    ]),
    ...mapActions('version', [
      'checkVersion',
    ]),
    ...mapActions('visual', [
      'runVisuals',
    ]),
    ...mapActions('visualConnectors', [
      'setConnectors',
    ]),
    ...mapActions('nodes', [
      'checkRootNode',
      'repositionChildren',
    ]),

    /* Keys events */
    keyDownHandler(event) {
      const container = this.$refs.container;
      if (!container) {
        return false;
      }

      // Spacebar
      if (event.keyCode === 32) {
        event.preventDefault();
      }
      if (event.keyCode === 32) {
        event.preventDefault();
        this.allowDragScroll = true;
        container.addEventListener('mousedown', this.mouseDownHandler);
      }
    },
    keyUpHandler(event) {
      const container = this.$refs.container;
      if (!container) {
        return false;
      }

      // Spacebar
      if (event.keyCode === 32) {
        event.preventDefault();
        container.removeEventListener('mousedown', this.mouseDownHandler);

        this.allowDragScroll = false;
      }
    },

    /* Dragging */
    mouseDownHandler(event) {
      const container = this.$refs.container;

      if (!this.allowDragScroll) {
        return;
      }
      this.isDragScroll = true;

      this.scrollPosition = {
        left: container.scrollLeft,
        top: container.scrollTop,
        // Get the current mouse position
        x: event.clientX,
        y: event.clientY,
      };

      document.addEventListener('mousemove', this.mouseMoveHandler);
      document.addEventListener('mouseup', this.mouseUpHandler);
    },
    touchStartHandler(event) {
      const container = this.$refs.container;

      this.isDragScroll = true;
      const touch = event.touches[0] || event.changedTouches[0];

      this.scrollPosition = {
        left: container.scrollLeft,
        top: container.scrollTop,
        // Get the current mouse position
        x: touch.pageX,
        y: touch.pageY,
      };
    },
    mouseMoveHandler(event) {
      const container = this.$refs.container;

      // How far the mouse has been moved
      const dx = event.clientX - this.scrollPosition.x;
      const dy = event.clientY - this.scrollPosition.y;

      // Scroll the element
      container.scrollTop = this.scrollPosition.top - dy;
      container.scrollLeft = this.scrollPosition.left - dx;
    },
    touchMoveHandler(event) {
      const container = this.$refs.container;

      const touch = event.touches[0] || event.changedTouches[0];
      // How far the mouse has been moved
      const dx = touch.pageX - this.scrollPosition.x;
      const dy = touch.pageY - this.scrollPosition.y;

      // Scroll the element
      container.scrollTop = this.scrollPosition.top - dy;
      container.scrollLeft = this.scrollPosition.left - dx;
    },
    mouseUpHandler() {
      this.isDragScroll = false;

      document.removeEventListener('mousemove', this.mouseMoveHandler);
      document.removeEventListener('mouseup', this.mouseUpHandler);
    },

    /* Init events */
    async initEvents() {
      await this.$nextTick();

      const container = this.$refs.container;
      if (!container) {
        return false;
      }

      if (!this.isDraggingNode
        && !this.editingNodeText
      ) {
        // Touch
        container.addEventListener('touchstart', this.touchStartHandler);
        container.addEventListener('touchmove', this.touchMoveHandler);

        // Space bar
        document.addEventListener('keydown', this.keyDownHandler);
        document.addEventListener('keyup', this.keyUpHandler);
      } else {
        // Touch
        container.removeEventListener('touchstart', this.touchStartHandler);
        container.removeEventListener('touchmove', this.touchMoveHandler);

        // Space bar
        document.removeEventListener('keydown', this.keyDownHandler);
        document.removeEventListener('keyup', this.keyUpHandler);
      }
    },
  },
  watch: {
    async items() {
      this.$nextTick(async () => {
        await this.resetTempVariables();
        await this.runVisuals();
      });
    },
    async layoutScale() {
      this.$nextTick(async () => {
        await this.resetTempVariables();
        await this.runVisuals();
      });
    },
    isDraggingNode() {
      this.$nextTick(() => {
        this.initEvents();
      });
    },
    editingNodeText() {
      this.$nextTick(() => {
        this.initEvents();
      });
    },
  },
};
</script>
